<template>
  <!-- App info (DEV-1021). The screen that answers "is it me or the app?" from
       a field phone: which build, is the worker running, is the device full,
       did anyone ever grant the camera. Everything here is read from a Web API
       or /version.json, so it needs no configuration per tenant. -->
  <!-- ⚠️ MOUNTED AT APP LEVEL (shell/App.vue), and it must stay there.
       It used to be mounted from SidebarFooter — i.e. INSIDE the sidebar's own
       <nav>, which Vuetify slides with a `transform`. A transformed ancestor
       becomes the containing block for every `position: fixed` descendant, so
       this drawer sized itself against a 230px sidebar and `max-width: 100%`
       squashed it to 229px, mid-screen (seen on a real screen, 2026-09-14).
       The v-dialog it replaced never hit that, because Vuetify overlays
       teleport to body; a navigation drawer renders in place.

       A `<Teleport to=".v-application__wrap">` was tried as the fix and is the
       WRONG one here: that element is rendered BY <v-app>, this component's own
       parent, so it does not exist yet at mount — "Failed to locate Teleport
       target". Mounting at app level solves it with no teleport at all, and
       `AppInfoDrawer.test.ts` pins the mount point. -->
  <v-navigation-drawer
    :model-value="appInfoOpen"
    location="right"
    temporary
    :width="width"
    :border="0"
    class="ai"
    @update:model-value="close"
  >
    <div class="ai__shell">
      <header class="ai__head">
        <h2 class="ai__title">{{ t("appInfo.title") }}</h2>
        <v-btn
          icon="mdi-close" variant="text" size="small"
          :aria-label="t('common.close')" @click="close(false)"
        />
      </header>

      <div class="ai__body">
        <section class="ai__section">
          <h3 class="ai__heading">{{ t("appInfo.build") }}</h3>
          <dl class="ai__rows">
            <div class="ai__row">
              <dt>{{ t("appInfo.released") }}</dt>
              <dd>{{ version ? formatBuiltAt(version.builtAt, locale) : "—" }}</dd>
            </div>
            <div class="ai__row">
              <dt>{{ t("appInfo.release") }}</dt>
              <dd class="ai__release">{{ version?.releaseName ?? "—" }}</dd>
            </div>
            <div class="ai__row">
              <dt>{{ t("appInfo.buildId") }}</dt>
              <dd class="ai__mono">{{ version?.shaShort ?? "—" }}</dd>
            </div>
            <div class="ai__row">
              <dt>{{ t("appInfo.offlineReady") }}</dt>
              <dd>
                <span class="ai__pill" :class="workerPillClass">{{ t(`appInfo.worker.${workerState}`) }}</span>
              </dd>
            </div>
          </dl>
        </section>

        <section v-if="storage" class="ai__section">
          <h3 class="ai__heading">{{ t("appInfo.storage") }}</h3>
          <p class="ai__storage">
            {{ t("appInfo.storageUsed", { used: formatBytes(storage.usage), total: formatBytes(storage.quota) }) }}
          </p>
          <div v-if="storage.percent !== null" class="ai__bar" role="presentation">
            <div class="ai__bar-fill" :style="{ width: `${storage.percent.toFixed(1)}%` }"></div>
          </div>
        </section>

        <section class="ai__section">
          <h3 class="ai__heading">{{ t("appInfo.permissions") }}</h3>
          <dl class="ai__rows">
            <div v-for="key in permissionKeys" :key="key" class="ai__row">
              <dt>{{ t(`appInfo.permission.${key}`) }}</dt>
              <dd class="ai__perm">
                <span class="ai__pill" :class="permissionPillClass(permissions[key])">
                  {{ t(`appInfo.permissionState.${permissions[key]}`) }}
                </span>
                <!-- Only where the browser will still ask. Once denied it will
                     not prompt again from script, so a button there would be a
                     lie; the hint points at site settings instead. -->
                <v-btn
                  v-if="canRequest(permissions[key])"
                  size="x-small" variant="tonal" :loading="requesting === key"
                  @click="turnOn(key)"
                >
                  {{ t("appInfo.turnOn") }}
                </v-btn>
                <span v-else-if="permissions[key] === 'denied'" class="ai__hint">
                  {{ t("appInfo.deniedHint") }}
                </span>
              </dd>
            </div>
          </dl>
        </section>

        <!-- Which BUNDLE each module resolved to. The shell being current does
             not mean the modules are: a tenant app reads its import map from a
             cached endpoint, and on 2026-09-09 that served the previous boer
             bundle for ~25 minutes while every other indicator looked healthy. -->
        <section v-if="modules.length" class="ai__section">
          <h3 class="ai__heading">{{ t("appInfo.modules") }}</h3>
          <dl class="ai__rows">
            <div v-for="m in modules" :key="m.name" class="ai__row">
              <dt>{{ m.name.replace("@metaship/", "") }}</dt>
              <dd>
                <span class="ai__mono">{{ m.bundle }}</span>
                <span v-if="m.loaded" class="ai__pill ai__pill--good">{{ t("appInfo.moduleLoaded") }}</span>
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <footer class="ai__actions">
        <!-- Clears caches and workers, keeps the session and any queued
             offline work — the difference between this and signing out. -->
        <v-btn variant="text" size="small" class="ai__reinstall" @click="reinstall">
          {{ t("appInfo.forceReinstall") }}
        </v-btn>
        <v-btn variant="text" size="small" :loading="appUpdate.checking.value" @click="check">
          {{ checkLabel }}
        </v-btn>
        <v-btn variant="tonal" size="small" @click="close(false)">{{ t("common.close") }}</v-btn>
      </footer>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import {
  fetchBuildVersion,
  formatBuiltAt,
  formatBytes,
  permissionStates,
  requestPermission,
  canRequest,
  resolvedModules,
  type ResolvedModule,
  serviceWorkerState,
  storageUsage,
  type BuildVersion,
  type PermissionKey,
  type PermissionValue,
  type StorageUsage,
  type WorkerState,
} from "@/pwa/appInfo";
import { appUpdate, checkForUpdate, forceReinstall } from "@/pwa/appUpdate";
import { appInfoOpen, closeAppInfo } from "@/shell/appInfoState";

// No props: App info is opened from the account menu AND from a module's
// Settings, so the state lives in `appInfoState.ts` and this mounts once.

const { t, locale } = useI18n();
const { mobile, width: viewport } = useDisplay();

/**
 * A right slide-over, per the platform's design language — not a modal.
 *
 * On a phone it takes all but a sliver of the screen: this panel exists to be
 * read when something is wrong ("is it me or the app?"), and a 480px card
 * floating in the middle of a 412px screen is the worst of both.
 */
const width = computed(() => (mobile.value ? Math.max(viewport.value - 32, 280) : 480));

const permissionKeys: PermissionKey[] = ["camera", "geolocation", "notifications", "persistentStorage"];
const modules = ref<ResolvedModule[]>([]);
const requesting = ref<PermissionKey | null>(null);

/**
 * Ask the browser for one permission, then re-read ALL of them.
 *
 * Re-reading rather than trusting the return value: granting persistent
 * storage can flip other answers, and the panel's job is to be accurate about
 * the device, not optimistic about the click.
 */
async function turnOn(key: PermissionKey): Promise<void> {
  requesting.value = key;
  try {
    await requestPermission(key);
    permissions.value = await permissionStates();
  } finally {
    requesting.value = null;
  }
}

const version = ref<BuildVersion | null>(null);
const workerState = ref<WorkerState>("none");
const storage = ref<StorageUsage | null>(null);
const permissions = ref<Record<PermissionKey, PermissionValue>>({
  camera: "unsupported",
  geolocation: "unsupported",
  notifications: "unsupported",
  persistentStorage: "unsupported",
});
/** Set after an explicit check that found nothing, so the button can say so. */
const upToDate = ref(false);

const checkLabel = computed(() =>
  upToDate.value && !appUpdate.ready.value ? t("appInfo.upToDate") : t("appInfo.checkForUpdates"),
);

const workerPillClass = computed(() =>
  workerState.value === "active" ? "ai__pill--good" : workerState.value === "waiting" ? "ai__pill--warn" : "",
);

function permissionPillClass(value: PermissionValue): string {
  if (value === "granted") return "ai__pill--good";
  if (value === "denied") return "ai__pill--bad";
  return "";
}

// Read on open, not on mount: a storage estimate and four permission queries
// are wasted work for a dialog nobody opens, and stale by the time they do.
watch(
  () => appInfoOpen.value,
  async (open) => {
    if (!open) return;
    upToDate.value = false;
    const [v, w, s, p] = await Promise.all([
      fetchBuildVersion(),
      serviceWorkerState(),
      storageUsage(),
      permissionStates(),
    ]);
    version.value = v;
    workerState.value = w;
    storage.value = s;
    permissions.value = p;
    // Read from the live import map, not fetched — it is already in the DOM.
    modules.value = resolvedModules();
  },
  { immediate: true },
);

function close(_value?: boolean): void {
  closeAppInfo();
}

async function check(): Promise<void> {
  const found = await checkForUpdate();
  upToDate.value = !found;
  workerState.value = await serviceWorkerState();
  // A found update raises the banner; close so it is not hidden behind this.
  if (found) close(false);
}

async function reinstall(): Promise<void> {
  await forceReinstall();
}
</script>

<style scoped>
/* A drawer is a full-height column: a fixed head, a body that scrolls on its
   own, and actions that stay reachable without scrolling to them. */
/* ⚠️ ABOVE the left sidebar, which Vuetify puts at z-index 1006.
   Vuetify assigns drawer z-indexes by layout registration order, and this one
   landed at 1004 — so the nav rendered ON TOP of it. Invisible on a desktop,
   where the two never overlap; obvious on a phone, where this drawer is nearly
   full width and the nav sits over it (seen on a real handset, 2026-09-14).
   Forced, and `!important`, because the number Vuetify computes is not a number
   this component gets a say in. The scrim is raised with it, or the sidebar
   would sit above the dimming too. */
.ai,
.ai + .v-navigation-drawer__scrim,
:deep(.v-navigation-drawer__scrim) {
  z-index: 2500 !important;
}

/* The name is the line a person reads out on the phone — give it the weight. */
.ai__release {
  font-weight: 600;
}

.ai__shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.ai__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 8px 10px 20px;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.12);
  flex: none;
}

.ai__title {
  font-size: 17px;
  font-weight: 600;
  margin: 0;
}

.ai__body {
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 18px 20px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.ai__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai__heading {
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.6;
}

.ai__rows {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ai__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.1);
}

.ai__row:last-child {
  border-bottom: 0;
}

.ai__row dt {
  font-size: 13.5px;
  opacity: 0.78;
}

.ai__row dd {
  margin: 0;
  font-size: 13.5px;
  text-align: right;
}

.ai__mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 12.5px;
}

.ai__pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  background: rgba(var(--v-border-color), 0.14);
}

.ai__pill--good {
  background: rgba(76, 175, 80, 0.16);
  color: rgb(56, 142, 60);
}

.ai__pill--warn {
  background: rgba(255, 152, 0, 0.18);
  color: rgb(191, 108, 0);
}

.ai__pill--bad {
  background: rgba(244, 67, 54, 0.16);
  color: rgb(198, 40, 40);
}

.ai__storage {
  margin: 0;
  font-size: 13.5px;
}

.ai__bar {
  height: 6px;
  border-radius: 3px;
  background: rgba(var(--v-border-color), 0.16);
  overflow: hidden;
}

.ai__bar-fill {
  height: 100%;
  background: rgb(var(--v-theme-primary));
}

.ai__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  /* WRAPS, deliberately. Three actions do not fit 480px once the labels are
     Afrikaans — measured in the browser: the footer overflowed by 8px and
     "Maak toe" ran past the screen edge. English happens to fit, which is
     exactly how a layout ships broken for everyone else. */
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(var(--v-border-color), 0.12);
  flex: none;
  padding: 8px 16px 12px;
}

/* Pushed left while it shares a row, first to drop when it cannot. */
.ai__reinstall {
  margin-inline-end: auto;
  opacity: 0.7;
}
.ai__perm { display: flex; align-items: center; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }
.ai__hint { font-size: .72rem; opacity: .7; }
.ai__mono { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: .74rem; margin-right: 6px; }
</style>
